/**
 * NewRelic Tracking Utility for Firework Video Components
 * Provides comprehensive event tracking for StoryBlock and VideoGrid components
 */

export interface FireworkEventDetail {
  video?: {
    encoded_id?: string;
    caption?: string;
    badge?: string;
    hashtags?: string[];
    duration?: number;
    width?: number;
    height?: number;
    live_stream_status?: string;
    status?: string;
    autoplay?: boolean;
    has_cta?: boolean;
  };
  videoId?: string;
  product?: {
    product_id?: string;
    product_sku?: string;
    product_name?: string;
    product_price?: number;
    product_url?: string;
  };
  transitionTo?: string;
  transitionFrom?: string;
  progress?: number;
  state?: string;
  pollId?: string;
  pollQuestion?: string;
  choiceId?: string;
  giveawayId?: string;
  message?: string;
  reaction?: string;
  unit_id?: string;
}

export interface NewRelicEventAttributes {
  analyticsProvider: "newrelic";
  component: string;
  componentPrefix: string;
  sectionTitle?: string;
  channelId?: string;
  playlistId?: string;
  timestamp: string;
  videoId?: string;
  contentTitle?: string;
  videoBadge?: string;
  videoHashtags?: string;
  videoDuration?: number;
  videoProgress?: number;
  videoState?: string;
  videoWidth?: number;
  videoHeight?: number;
  videoAutoplay?: boolean;
  videoHasCta?: boolean;
  liveStatus?: string;
  productId?: string;
  productSku?: string;
  productName?: string;
  productPrice?: number;
  productUrl?: string;
  transitionTo?: string;
  transitionFrom?: string;
  pollId?: string;
  pollQuestion?: string;
  choiceId?: string;
  giveawayId?: string;
  message?: string;
  reaction?: string;
  unitId?: string;
  itemSequence?: string | null;
  watchProgress?: string;
}

export const FIREWORK_EVENTS = {
  VIDEO: {
    IMPRESSION: "fw:video:impression",
    START: "fw:video:start",
    FIRST_QUARTILE: "fw:video:first-quartile",
    MIDPOINT: "fw:video:midpoint",
    THIRD_QUARTILE: "fw:video:third-quartile",
    COMPLETE: "fw:video:complete",
    SESSION: "fw:video:session",
    INTERACTABLE_ENGAGED_VIEW: "fw:video:interactable-engaged-view",
    CLICK_CTA: "fw:video:click-cta",
    CLICK_SHARE: "fw:video:click-share",
    SHARE: "fw:video:share",
    SUBMIT_ANSWER: "fw:video:submit-answer",
    SUBMIT_CHOICE: "fw:video:submit-choice",
  },
  LIVESTREAM: {
    JOIN: "fw:livestream:join",
    QUIT: "fw:livestream:quit",
    SEND_CHAT: "fw:livestream:send-chat",
    SEND_HEART: "fw:livestream:send-heart",
  },
  PLAYER: {
    TRANSITION: "fw:player:transition",
    QUIT: "fw:player:quit",
    NAVIGATE: "fw:player:navigate",
  },
  SHOPPING: {
    PRODUCT_CLICKED: "fw:shopping:product-clicked",
    PRODUCT_LIST_ITEM_IMPRESSION: "fw:shopping:product-list-item-impression",
    PRODUCT_LIST_ITEM_CLICKED: "fw:shopping:product-list-item-clicked",
    PRODUCT_CARD_IMPRESSION: "fw:shopping:product-card-impression",
    PRODUCT_CARD_CLICKED: "fw:shopping:product-card-clicked",
    PINNED_PRODUCT_IMPRESSION: "fw:shopping:pinned-product-impression",
    PINNED_PRODUCT_CLICKED: "fw:shopping:pinned-product-clicked",
    PDP_PRIMARY_CTA_CLICKED: "fw:shopping:pdp-primary-cta-clicked",
    REQUEST_CHECKOUT: "fw:shopping:request-checkout",
  },
  INTERACTION: {
    SUBMIT_ANSWER: "fw:interaction:submit-answer",
    SUBMIT_CHOICE: "fw:interaction:submit-choice",
    GIVEAWAY_WINNER_CHOSEN: "fw:interaction:giveaway:winner-chosen",
  },
  EMBED: {
    ERROR: "fw:embed-feed:error",
    RENDER: "fw:embed-feed:render",
    CONTENT_READY: "fw:embed-feed:content-ready",
    IMPRESSION: "fw:embed-feed:impression",
    THUMBNAIL_LOAD: "fw:embed-feed:thumbnail-load",
    THUMBNAIL_CLICK: "fw:embed-feed:thumbnail-click",
    STORYBLOCK_RENDER: "fw:storyblock:render",
    STORYBLOCK_IMPRESSION: "fw:storyblock:impression",
  },
} as const;

const EVENT_NAME_MAP: Record<string, string> = {
  [FIREWORK_EVENTS.VIDEO.IMPRESSION]: "VIDEO_IMPRESSION",
  [FIREWORK_EVENTS.VIDEO.START]: "VIDEO_START",
  [FIREWORK_EVENTS.VIDEO.FIRST_QUARTILE]: "VIDEO_FIRST_QUARTILE",
  [FIREWORK_EVENTS.VIDEO.MIDPOINT]: "VIDEO_MIDPOINT",
  [FIREWORK_EVENTS.VIDEO.THIRD_QUARTILE]: "VIDEO_THIRD_QUARTILE",
  [FIREWORK_EVENTS.VIDEO.COMPLETE]: "VIDEO_COMPLETE",
  [FIREWORK_EVENTS.VIDEO.SESSION]: "VIDEO_SESSION",
  [FIREWORK_EVENTS.VIDEO.INTERACTABLE_ENGAGED_VIEW]: "VIDEO_INTERACTABLE_ENGAGED_VIEW",
  [FIREWORK_EVENTS.VIDEO.CLICK_CTA]: "VIDEO_CLICK_CTA",
  [FIREWORK_EVENTS.VIDEO.CLICK_SHARE]: "VIDEO_CLICK_SHARE",
  [FIREWORK_EVENTS.VIDEO.SHARE]: "VIDEO_SHARE",
  [FIREWORK_EVENTS.VIDEO.SUBMIT_ANSWER]: "VIDEO_SUBMIT_ANSWER",
  [FIREWORK_EVENTS.VIDEO.SUBMIT_CHOICE]: "VIDEO_SUBMIT_CHOICE",
  [FIREWORK_EVENTS.LIVESTREAM.JOIN]: "LIVESTREAM_JOIN",
  [FIREWORK_EVENTS.LIVESTREAM.QUIT]: "LIVESTREAM_QUIT",
  [FIREWORK_EVENTS.LIVESTREAM.SEND_CHAT]: "LIVESTREAM_SEND_CHAT",
  [FIREWORK_EVENTS.LIVESTREAM.SEND_HEART]: "LIVESTREAM_SEND_HEART",
  [FIREWORK_EVENTS.PLAYER.TRANSITION]: "PLAYER_TRANSITION",
  [FIREWORK_EVENTS.PLAYER.QUIT]: "PLAYER_QUIT",
  [FIREWORK_EVENTS.PLAYER.NAVIGATE]: "PLAYER_NAVIGATE",
  [FIREWORK_EVENTS.SHOPPING.PRODUCT_CLICKED]: "SHOPPING_PRODUCT_CLICKED",
  [FIREWORK_EVENTS.SHOPPING.PRODUCT_LIST_ITEM_IMPRESSION]: "SHOPPING_PRODUCT_LIST_ITEM_IMPRESSION",
  [FIREWORK_EVENTS.SHOPPING.PRODUCT_LIST_ITEM_CLICKED]: "SHOPPING_PRODUCT_LIST_ITEM_CLICKED",
  [FIREWORK_EVENTS.SHOPPING.PRODUCT_CARD_IMPRESSION]: "SHOPPING_PRODUCT_CARD_IMPRESSION",
  [FIREWORK_EVENTS.SHOPPING.PRODUCT_CARD_CLICKED]: "SHOPPING_PRODUCT_CARD_CLICKED",
  [FIREWORK_EVENTS.SHOPPING.PINNED_PRODUCT_IMPRESSION]: "SHOPPING_PINNED_PRODUCT_IMPRESSION",
  [FIREWORK_EVENTS.SHOPPING.PINNED_PRODUCT_CLICKED]: "SHOPPING_PINNED_PRODUCT_CLICKED",
  [FIREWORK_EVENTS.SHOPPING.PDP_PRIMARY_CTA_CLICKED]: "SHOPPING_PDP_PRIMARY_CTA_CLICKED",
  [FIREWORK_EVENTS.SHOPPING.REQUEST_CHECKOUT]: "SHOPPING_REQUEST_CHECKOUT",
  [FIREWORK_EVENTS.INTERACTION.SUBMIT_ANSWER]: "INTERACTION_SUBMIT_ANSWER",
  [FIREWORK_EVENTS.INTERACTION.SUBMIT_CHOICE]: "INTERACTION_SUBMIT_CHOICE",
  [FIREWORK_EVENTS.INTERACTION.GIVEAWAY_WINNER_CHOSEN]: "INTERACTION_GIVEAWAY_WINNER_CHOSEN",
  [FIREWORK_EVENTS.EMBED.ERROR]: "EMBED_FEED_ERROR",
  [FIREWORK_EVENTS.EMBED.RENDER]: "EMBED_FEED_RENDER",
  [FIREWORK_EVENTS.EMBED.CONTENT_READY]: "EMBED_FEED_CONTENT_READY",
  [FIREWORK_EVENTS.EMBED.IMPRESSION]: "EMBED_FEED_IMPRESSION",
  [FIREWORK_EVENTS.EMBED.THUMBNAIL_LOAD]: "EMBED_FEED_THUMBNAIL_LOAD",
  [FIREWORK_EVENTS.EMBED.THUMBNAIL_CLICK]: "EMBED_FEED_THUMBNAIL_CLICK",
  [FIREWORK_EVENTS.EMBED.STORYBLOCK_RENDER]: "STORYBLOCK_RENDER",
  [FIREWORK_EVENTS.EMBED.STORYBLOCK_IMPRESSION]: "STORYBLOCK_IMPRESSION",
};

function getWatchProgress(eventType: string, progress?: number): string | undefined {
  if (progress !== undefined) {
    return `${Math.round(progress * 100)}%`;
  }

  const progressMap: Record<string, string> = {
    [FIREWORK_EVENTS.VIDEO.FIRST_QUARTILE]: "25%",
    [FIREWORK_EVENTS.VIDEO.MIDPOINT]: "50%",
    [FIREWORK_EVENTS.VIDEO.THIRD_QUARTILE]: "75%",
    [FIREWORK_EVENTS.VIDEO.COMPLETE]: "90%+",
  };

  return progressMap[eventType];
}

function extractVideoMetadata(detail: FireworkEventDetail): Partial<NewRelicEventAttributes> {
  const video = detail?.video;

  return {
    videoId: video?.encoded_id ?? detail?.videoId,
    contentTitle: video?.caption,
    videoBadge: video?.badge,
    videoHashtags: video?.hashtags?.join(", "),
    videoDuration: video?.duration,
    videoWidth: video?.width,
    videoHeight: video?.height,
    videoAutoplay: video?.autoplay,
    videoHasCta: video?.has_cta,
    liveStatus: video?.status ?? video?.live_stream_status,
    videoProgress: detail?.progress,
    videoState: detail?.state,
  };
}

function extractShoppingMetadata(detail: FireworkEventDetail): Partial<NewRelicEventAttributes> {
  const product = detail?.product;

  if (!product) return {};

  return {
    productId: product.product_id,
    productSku: product.product_sku,
    productName: product.product_name,
    productPrice: product.product_price,
    productUrl: product.product_url,
  };
}

function extractInteractionMetadata(detail: FireworkEventDetail): Partial<NewRelicEventAttributes> {
  return {
    transitionTo: detail?.transitionTo,
    transitionFrom: detail?.transitionFrom,
    pollId: detail?.pollId,
    pollQuestion: detail?.pollQuestion,
    choiceId: detail?.choiceId,
    giveawayId: detail?.giveawayId,
    message: detail?.message,
    reaction: detail?.reaction,
    unitId: detail?.unit_id,
  };
}

/**
 * Main function to track Firework events in NewRelic
 */
export function trackFireworkEventInNR(
  component: "StoryBlock" | "VideoGrid",
  eventType: string,
  event: Event,
  options: {
    sectionTitle?: string;
    channelId?: string;
    playlistId?: string;
    itemSequence?: string | null;
  } = {}
): void {
  if (typeof window === "undefined") {
    return;
  }

  if (!window.newrelic) {
    const maxRetries = 10;
    const retryDelay = 200;

    const waitForNewRelic = (retryCount = 0) => {
      if (window.newrelic) {
        trackFireworkEventInNR(component, eventType, event, options);
      } else if (retryCount < maxRetries) {
        const delay = retryDelay * 1.5 ** retryCount;
        setTimeout(() => waitForNewRelic(retryCount + 1), delay);
      }
    };

    waitForNewRelic();
    return;
  }

  const customEvent = event as CustomEvent<FireworkEventDetail>;
  const detail = customEvent?.detail ?? {};

  const mappedEventName =
    EVENT_NAME_MAP[eventType] ?? eventType.toUpperCase().replace(/[:-]/g, "_");

  let componentPrefix = component === "StoryBlock" ? "STORYBLOCK" : "VIDEOGRID";
  if (component === "VideoGrid" && options.sectionTitle) {
    componentPrefix = options.sectionTitle.toUpperCase().replace(/\s+/g, "_");
  }

  const eventName = `${componentPrefix}_${mappedEventName}`;

  const attributes: NewRelicEventAttributes = {
    analyticsProvider: "newrelic",
    component,
    componentPrefix,
    timestamp: new Date().toISOString(),
    ...options,
    ...extractVideoMetadata(detail),
    ...extractShoppingMetadata(detail),
    ...extractInteractionMetadata(detail),
    watchProgress: getWatchProgress(eventType, detail.progress),
  };

  const cleanedAttributes = Object.fromEntries(
    Object.entries(attributes).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );

  try {
    window.newrelic.addPageAction(eventName, cleanedAttributes);
  } catch {
    // Silently fail - NewRelic tracking should not break the application
  }
}

/**
 * Create event handler factory for a component
 */
export function createFireworkEventHandler(
  component: "StoryBlock" | "VideoGrid",
  options: {
    sectionTitle?: string;
    channelId?: string;
    playlistId?: string;
    getItemSequence?: () => string | null | undefined;
  } = {}
) {
  return (eventType: string) =>
    (event: Event): void => {
      trackFireworkEventInNR(component, eventType, event, {
        ...options,
        itemSequence: options.getItemSequence?.() ?? undefined,
      });
    };
}
